import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Student {
  id: number;
  student_id: number | null;
  first_name: string;
  family_name: string;
  combined_name: string;
  email: string;
  phone_number: string | null;
  address: string | null;
  home_country: string | null;
  gender: string | null;
  marital_status: string | null;
  major: string | null;
  program_level: string | null;
  special_interests: string | null;
  has_dietary_restrictions: boolean | null;
  dietary_restrictions_details: string | null;
  willing_to_share: boolean | null;
  vaccination_status: string | null;
  semester_year: string | null;
  semester_id: number | null;
  time_submitted: string | null;
  created_at: string;
  updated_at: string;
}

interface Assignment {
  id: number;
  family_id: number;
  family_name: string;
  semester_name: string;
  semester_id: number;
  family_email_sent_at: string | null;
  student_email_sent_at: string | null;
  created_at: string;
}

interface StudentModalProps {
  studentId: number;
  isOpen: boolean;
  onClose: () => void;
  onStudentUpdated?: (student: Student) => void;
}

export const StudentModal: React.FC<StudentModalProps> = ({
  studentId,
  isOpen,
  onClose,
  onStudentUpdated
}) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [editedStudent, setEditedStudent] = useState<Student | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && studentId) {
      loadStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId]);

  const loadStudentData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load student details
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('*')
        .eq('id', studentId)
        .single();

      if (studentError) throw studentError;
      
      setStudent(studentData);
      setEditedStudent({ ...studentData });

      // Load assignment history
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('assignments')
        .select('id, family_id, semester_id, family_email_sent_at, student_email_sent_at, created_at')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });

      if (assignmentError) throw assignmentError;

      // Load family names separately
      const familyIds = assignmentData?.map(a => a.family_id).filter(Boolean) || [];
      let familyNames: Record<number, string> = {};
      
      if (familyIds.length > 0) {
        const { data: familyData } = await supabase
          .from('families')
          .select('id, combined_name')
          .in('id', familyIds);
        
        familyNames = familyData?.reduce((acc, family) => ({
          ...acc,
          [family.id]: family.combined_name
        }), {}) || {};
      }

      // Load semester names for assignments
      const assignmentSemesterIds = assignmentData?.map(a => a.semester_id).filter(Boolean) || [];
      let semesterNames: Record<number, string> = {};
      
      if (assignmentSemesterIds.length > 0) {
        const { data: semesterData } = await supabase
          .from('semesters')
          .select('id, name')
          .in('id', assignmentSemesterIds);
        
        semesterNames = semesterData?.reduce((acc, semester) => ({
          ...acc,
          [semester.id]: semester.name
        }), {}) || {};
      }

      const formattedAssignments = assignmentData?.map(a => ({
        ...a,
        family_name: familyNames[a.family_id] || 'Unknown Family',
        semester_name: semesterNames[a.semester_id] || 'Unknown Semester'
      })) || [];

      setAssignments(formattedAssignments); // Update assignments
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!editedStudent?.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!editedStudent?.family_name?.trim()) {
      errors.family_name = 'Family name is required';
    }
    if (!editedStudent?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedStudent.email)) {
      errors.email = 'Invalid email format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!editedStudent || !validateForm()) return;

    setIsSaving(true);
    setError(null);

    try {
      // Update combined_name based on first and family names
      const updatedStudent = {
        ...editedStudent,
        combined_name: `${editedStudent.family_name}, ${editedStudent.first_name}`,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('students')
        .update(updatedStudent)
        .eq('id', studentId)
        .select()
        .single();

      if (error) throw error;

      setStudent(data);
      setEditedStudent({ ...data });
      setIsEditing(false);

      if (onStudentUpdated) {
        onStudentUpdated(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save student');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (student) {
      setEditedStudent({ ...student });
    }
    setIsEditing(false);
    setValidationErrors({});
    setError(null);
  };

  const handleFieldChange = (field: keyof Student, value: any) => {
    if (!editedStudent) return;
    
    setEditedStudent({
      ...editedStudent,
      [field]: value
    });

    // Clear validation error when field is changed
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#2a2a2a',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #444',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#333'
        }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            Student Information
            {student?.gender && (
              <span style={{ 
                marginLeft: '10px', 
                color: student.gender === 'Male' ? '#87CEEB' : '#FFB6C1',
                fontSize: '0.9em' 
              }}>
                ({student.gender === 'Male' ? 'M' : 'F'})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#ccc',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'white' }}>
              Loading student information...
            </div>
          ) : error ? (
            <div style={{
              padding: '20px',
              color: '#ff6b6b',
              backgroundColor: '#2d1b1b',
              border: '1px solid #ff6b6b',
              margin: '20px',
              borderRadius: '4px'
            }}>
              Error: {error}
            </div>
          ) : student ? (
            <div style={{ padding: '20px' }}>
              {/* Basic Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  Basic Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {renderField('first_name', 'First Name', editedStudent?.first_name || '')}
                  {renderField('family_name', 'Family Name', editedStudent?.family_name || '')}
                  {renderField('email', 'Email', editedStudent?.email || '')}
                  {renderField('phone_number', 'Phone Number', editedStudent?.phone_number || '')}
                  {renderField('address', 'Address', editedStudent?.address || '')}
                  {renderField('home_country', 'Home Country', editedStudent?.home_country || '')}
                </div>
              </div>

              {/* Academic Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  Academic Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {renderField('major', 'Major', editedStudent?.major || '')}
                  {renderField('program_level', 'Program Level', editedStudent?.program_level || '')}
                  {renderField('semester_year', 'Semester Year', editedStudent?.semester_year || '')}
                  {renderField('special_interests', 'Special Interests', editedStudent?.special_interests || '', 'textarea')}
                </div>
              </div>

              {/* Personal Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  Personal Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {renderSelectField('gender', 'Gender', editedStudent?.gender || '', [
                    { value: '', label: 'Not specified' },
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' }
                  ])}
                  {renderSelectField('marital_status', 'Marital Status', editedStudent?.marital_status || '', [
                    { value: '', label: 'Not specified' },
                    { value: 'Single', label: 'Single' },
                    { value: 'Married', label: 'Married' },
                    { value: 'Other', label: 'Other' }
                  ])}
                  {renderSelectField('vaccination_status', 'Vaccination Status', editedStudent?.vaccination_status || '', [
                    { value: '', label: 'Not specified' },
                    { value: 'Fully Vaccinated', label: 'Fully Vaccinated' },
                    { value: 'Partially Vaccinated', label: 'Partially Vaccinated' },
                    { value: 'Not Vaccinated', label: 'Not Vaccinated' }
                  ])}
                </div>
              </div>

              {/* Dietary Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  Dietary Information
                </h3>
                
                <div style={{ marginBottom: '15px' }}>
                  {renderCheckboxField('has_dietary_restrictions', 'Has Dietary Restrictions', editedStudent?.has_dietary_restrictions || false)}
                </div>
                
                {editedStudent?.has_dietary_restrictions && (
                  <div>
                    {renderField('dietary_restrictions_details', 'Dietary Restrictions Details', editedStudent?.dietary_restrictions_details || '', 'textarea')}
                  </div>
                )}
                
                <div style={{ marginTop: '15px' }}>
                  {renderCheckboxField('willing_to_share', 'Willing to Share Meals', editedStudent?.willing_to_share || false)}
                </div>
              </div>

              {/* Assignment History */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  Assignment History ({assignments.length})
                </h3>
                
                {assignments.length === 0 ? (
                  <p style={{ color: '#ccc', fontStyle: 'italic' }}>
                    No assignment history found.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {assignments.map(assignment => (
                      <div key={assignment.id} style={{
                        backgroundColor: '#333',
                        padding: '15px',
                        borderRadius: '6px',
                        border: '1px solid #555'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <div style={{ color: 'white', fontWeight: 'bold', marginBottom: '5px' }}>
                              {assignment.family_name}
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                              Semester: {assignment.semester_name}
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                              Assigned: {new Date(assignment.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.8em' }}>
                            <div style={{ color: assignment.student_email_sent_at ? '#28a745' : '#6c757d' }}>
                              Student Email: {assignment.student_email_sent_at ? '✓ Sent' : '○ Not sent'}
                            </div>
                            <div style={{ color: assignment.family_email_sent_at ? '#28a745' : '#6c757d' }}>
                              Family Email: {assignment.family_email_sent_at ? '✓ Sent' : '○ Not sent'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {student && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #444',
            backgroundColor: '#333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
              Created: {new Date(student.created_at).toLocaleDateString()} | 
              Updated: {new Date(student.updated_at).toLocaleDateString()}
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.7 : 1
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Edit Student
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function renderField(field: keyof Student, label: string, value: string, type: 'input' | 'textarea' = 'input') {
    const hasError = validationErrors[field];
    
    return (
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', color: 'white', marginBottom: '5px', fontWeight: 'bold' }}>
          {label}:
        </label>
        {isEditing ? (
          <>
            {type === 'textarea' ? (
              <textarea
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: hasError ? '1px solid #ff6b6b' : '1px solid #666',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  resize: 'vertical'
                }}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: hasError ? '1px solid #ff6b6b' : '1px solid #666',
                  borderRadius: '4px',
                  fontSize: '0.9em'
                }}
              />
            )}
            {hasError && (
              <div style={{ color: '#ff6b6b', fontSize: '0.8em', marginTop: '4px' }}>
                {hasError}
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            color: '#ccc', 
            padding: '8px',
            backgroundColor: '#1a1a1a',
            borderRadius: '4px',
            minHeight: type === 'textarea' ? '60px' : 'auto'
          }}>
            {value || <em style={{ opacity: 0.6 }}>Not specified</em>}
          </div>
        )}
      </div>
    );
  }

  function renderSelectField(field: keyof Student, label: string, value: string, options: Array<{value: string, label: string}>) {
    return (
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', color: 'white', marginBottom: '5px', fontWeight: 'bold' }}>
          {label}:
        </label>
        {isEditing ? (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#444',
              color: 'white',
              border: '1px solid #666',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ 
            color: '#ccc', 
            padding: '8px',
            backgroundColor: '#1a1a1a',
            borderRadius: '4px'
          }}>
            {options.find(opt => opt.value === value)?.label || <em style={{ opacity: 0.6 }}>Not specified</em>}
          </div>
        )}
      </div>
    );
  }

  function renderCheckboxField(field: keyof Student, label: string, value: boolean) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => handleFieldChange(field, e.target.checked)}
          disabled={!isEditing}
          style={{
            width: '16px',
            height: '16px',
            cursor: isEditing ? 'pointer' : 'not-allowed'
          }}
        />
        <label style={{ color: 'white', fontWeight: 'bold' }}>
          {label}
        </label>
      </div>
    );
  }
};